/**
 * device_session.service.ts — 로그인·앱 열기 때의 기기 확인 단계(12 설계 §7-1·§7-3·§7-4).
 *
 *   로그인:  비밀번호 확인 → (공용 PC 면 등록 없이 통과) → 내 기기 목록 읽기
 *            → 이 기기가 이미 활성 / 전체 admin / 활성 3대 미만 → 등록하고 통과
 *            → 3대 가득 → { status:"limit" } (Login 화면이 E-3/E-4 패널을 띄움)
 *   앱 열기: 60일 미사용 → 로그아웃 / 해제된 기기 → 로그아웃 / 그 밖엔 마지막 접속 갱신·자동 로그인 기록
 *
 * 1단계는 판정 전부 클라이언트(§10-1). **rules·인덱스가 아직 배포되지 않아 읽기·쓰기가 거부되면 막지 않고 통과**한다
 * (fail-open) — A 묶음 배포 전에도 로그인이 되도록. 이유는 콘솔 경고로만 남긴다.
 */
import type { User } from "@/domain/user";
import { DEVICE_SEEN_THROTTLE_MS, MAX_DEVICES, type AccessLogSelfType, type DeviceDoc } from "@/domain/session";
import {
  activeDevicesOf,
  currentDeviceInfo,
  isInactiveExpired,
  revokeNoticeOf,
  type DeviceInfo,
  type LogoutNoticeKind,
} from "@/domain/device_info";
import { getMyDevice, listMyDevices, registerMyDevice, releaseMyDevice, touchMyDevice } from "./devices.repository";
import { writeSelfAccessLog, type SelfAccessLogInput } from "./access_log.repository";
import { applyPersistence } from "./auth.service";
import {
  getDeviceId,
  isSessionLogged,
  isSharedSession,
  markSessionLogged,
  markSharedSession,
  newSessionId,
  readAutoLoginLoggedAt,
  readLastActivity,
  writeAutoLoginLoggedAt,
  writeAutoLoginOffHint,
  writeLastActivity,
} from "@/lib/sessionLocal";

type LogDevice = SelfAccessLogInput["device"];

function logDeviceOf(id: string, d: DeviceDoc | null, info: DeviceInfo): NonNullable<LogDevice> {
  return {
    id,
    name: d?.name || info.defaultName,
    browser: d?.browser || info.browser,
    os: d?.os || info.os,
    kind: d?.kind ?? info.kind,
  };
}

/** 접속 기록 입력 만들기 */
export function selfLog(
  user: User,
  type: AccessLogSelfType,
  device: LogDevice,
  sharedPc = false,
): SelfAccessLogInput {
  return {
    uid: user.uid,
    login_id: user.loginId,
    name: user.name,
    type,
    device,
    shared_pc: sharedPc,
    tenants: user.tenants ?? [],
  };
}

export type DeviceGate =
  | { status: "ok"; deviceId: string | null; shared: boolean }
  | {
      status: "limit";
      /** 지금 활성인 다른 기기들(최근 접속 순) */
      devices: DeviceDoc[];
      thisDevice: DeviceInfo;
      /** 이 기기 문서가 해제 상태로 남아 있으면 그 값(이름 유지용) */
      existing: DeviceDoc | null;
    };

async function registerAndLog(
  user: User,
  deviceId: string,
  info: DeviceInfo,
  existing: DeviceDoc | null,
  loginType: "login" | "auto_login" = "login",
): Promise<void> {
  try {
    await registerMyDevice(user.uid, deviceId, {
      name: info.defaultName,
      browser: info.browser,
      os: info.os,
      kind: info.kind,
      session_id: newSessionId(),
      existing,
    });
  } catch (e) {
    console.warn("[device] 기기 등록 실패(통과)", e);
  }
  const dev = logDeviceOf(deviceId, existing, info);
  if (!existing || existing.status !== "active") await writeSelfAccessLog(selfLog(user, "device_register", dev));
  await writeSelfAccessLog(selfLog(user, loginType, dev));
  markSessionLogged();
  writeLastActivity();
  // 힌트·저장 방식을 이 사람 기기 문서 값으로 다시 맞춤 — 로그인 전 힌트가 틀렸어도(다른 사람 값 등) 여기서 바로잡힌다
  const off = existing ? existing.auto_login === false && existing.status === "active" : false;
  writeAutoLoginOffHint(off, { uid: user.uid, loginId: user.loginId });
  await applyPersistence(off ? "session" : "local").catch(() => undefined);
}

/** 비밀번호 확인 직후 기기 확인 단계 */
export async function prepareDeviceOnLogin(user: User, opts: { shared: boolean }): Promise<DeviceGate> {
  const info = currentDeviceInfo();
  if (opts.shared) {
    // 공용 PC — 기기 문서를 만들지 않고 3대 계산에서 뺀다(§13 ④). 기록만 "공용 PC"
    markSharedSession(true);
    await writeSelfAccessLog(selfLog(user, "login", { id: "", name: "공용 PC", browser: info.browser, os: info.os, kind: info.kind }, true));
    markSessionLogged();
    writeLastActivity();
    return { status: "ok", deviceId: null, shared: true };
  }
  markSharedSession(false);
  const deviceId = getDeviceId();
  let all: DeviceDoc[];
  try {
    all = await listMyDevices(user.uid);
  } catch (e) {
    console.warn("[device] 기기 목록을 읽지 못함(통과)", e);
    await writeSelfAccessLog(selfLog(user, "login", logDeviceOf(deviceId, null, info)));
    markSessionLogged();
    writeLastActivity();
    return { status: "ok", deviceId, shared: false };
  }
  const existing = all.find((d) => d.id === deviceId) ?? null;
  const others = activeDevicesOf(all).filter((d) => d.id !== deviceId);
  if (existing?.status === "active" || user.isAdmin || others.length < MAX_DEVICES) {
    await registerAndLog(user, deviceId, info, existing);
    return { status: "ok", deviceId, shared: false };
  }
  return { status: "limit", devices: others, thisDevice: info, existing };
}

/** E-3/E-4 [선택한 기기 해제 후 등록] — 실패하면 throw(화면이 오류 표시) */
export async function completeDeviceLimit(
  user: User,
  release: DeviceDoc,
  existing: DeviceDoc | null,
): Promise<string> {
  const info = currentDeviceInfo();
  const deviceId = getDeviceId();
  await releaseMyDevice(user.uid, release.id);
  await writeSelfAccessLog(
    selfLog(user, "device_release", { id: release.id, name: release.name, browser: release.browser, os: release.os, kind: release.kind }),
  );
  await registerAndLog(user, deviceId, info, existing);
  return deviceId;
}

export type RestoreResult =
  | { status: "ok"; deviceId: string | null; shared: boolean; device: DeviceDoc | null }
  | { status: "signout"; notice: LogoutNoticeKind; log: SelfAccessLogInput | null };

/**
 * 저장된 로그인으로 앱을 열 때(새로고침·새 탭·PWA 열기).
 * 60일 미사용·해제된 기기면 로그아웃, 아니면 마지막 접속 갱신 + (세션당 1회, 15분 억제) 자동 로그인 기록.
 */
export async function restoreSession(user: User): Promise<RestoreResult> {
  const now = Date.now();
  const info = currentDeviceInfo();
  const shared = isSharedSession();
  const deviceId = shared ? null : getDeviceId();
  const dev = deviceId ? logDeviceOf(deviceId, null, info) : null;

  if (isInactiveExpired(readLastActivity(), now)) {
    return { status: "signout", notice: "expired", log: selfLog(user, "expired", dev, shared) };
  }
  if (shared || !deviceId) {
    writeLastActivity(now);
    return { status: "ok", deviceId: null, shared: true, device: null };
  }

  let existing: DeviceDoc | null;
  try {
    existing = await getMyDevice(user.uid, deviceId);
  } catch (e) {
    console.warn("[device] 기기 문서를 읽지 못함(통과)", e);
    writeLastActivity(now);
    return { status: "ok", deviceId, shared: false, device: null };
  }

  if (existing) {
    const notice = revokeNoticeOf(existing, user.uid);
    if (notice) return { status: "signout", notice, log: null };
    if (isInactiveExpired(existing.last_seen_at?.getTime(), now)) {
      return { status: "signout", notice: "expired", log: selfLog(user, "expired", logDeviceOf(deviceId, existing, info)) };
    }
  } else {
    // 기능 적용 전부터 로그인돼 있던 기기 — 자리가 있으면 조용히 등록, 없으면 다시 로그인(E-3)
    let all: DeviceDoc[] = [];
    try {
      all = await listMyDevices(user.uid);
    } catch {
      /* 통과 */
    }
    if (!user.isAdmin && activeDevicesOf(all).length >= MAX_DEVICES) {
      return { status: "signout", notice: "device_required", log: null };
    }
    await registerAndLog(user, deviceId, info, null, "auto_login");
    writeLastActivity(now);
    return { status: "ok", deviceId, shared: false, device: null };
  }

  const seenAgo = now - (existing.last_seen_at?.getTime() ?? 0);
  if (seenAgo > DEVICE_SEEN_THROTTLE_MS) {
    try {
      await touchMyDevice(user.uid, deviceId);
    } catch (e) {
      console.warn("[device] 마지막 접속 갱신 실패(무시)", e);
    }
  }
  if (!isSessionLogged() && now - readAutoLoginLoggedAt() > DEVICE_SEEN_THROTTLE_MS) {
    await writeSelfAccessLog(selfLog(user, "auto_login", logDeviceOf(deviceId, existing, info)));
    writeAutoLoginLoggedAt(now);
  }
  markSessionLogged();
  writeLastActivity(now);

  // 자동 로그인을 꺼 둔 기기 — 지금 세션도 창을 닫으면 끝나게(§7-1, 즉시 적용)
  writeAutoLoginOffHint(!existing.auto_login, { uid: user.uid, loginId: user.loginId });
  if (!existing.auto_login) {
    try {
      await applyPersistence("session");
    } catch {
      /* 무시 */
    }
  }
  return { status: "ok", deviceId, shared: false, device: existing };
}
